import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { TranscriptMessage as TranscriptMessageType } from 'types';
import { maskSensitiveNumbers, formatTimestamp } from 'utils/helpers';

interface Badge {
  label: string;
  color: string;
  title?: string;
}

interface DetailRow {
  label: string;
  value: string | number;
}

interface TranscriptMessageProps {
  message: TranscriptMessageType;
  showRawStt?: boolean;
  chainLength?: number;
}

const formatMs = (ms: number): string => (ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.round(ms)}ms`);

const BADGE_STYLES: Record<string, string> = {
  amber: 'bg-amber-100 text-amber-800 border-amber-300',
  blue: 'bg-blue-100 text-blue-800 border-blue-300',
  slate: 'bg-slate-100 text-slate-700 border-slate-300',
  red: 'bg-red-100 text-red-800 border-red-300',
  indigo: 'bg-indigo-100 text-indigo-800 border-indigo-300',
  grey: 'bg-gray-100 text-gray-700 border-gray-300',
  purple: 'bg-purple-100 text-purple-800 border-purple-300',
};

// Per-phase label / tone / tooltip. Mirrors voicex's _current_bot_phase()
// return values. Use the bare phase key for any unknown value to keep the
// UI forward-compatible if backend adds a new phase before the type does.
const PHASE_META: Record<
  string,
  { label: string; tone: 'slate' | 'amber' | 'blue' | 'red'; tip: string }
> = {
  audio_playback: {
    label: 'while bot speaking',
    tone: 'slate',
    tip: 'Normal overlap — caller spoke while bot was actively transmitting audio.',
  },
  synth_in_flight: {
    label: 'pre-audio (synth)',
    tone: 'amber',
    tip: 'Caller spoke between LLM completion and audio reaching the wire. Cascade window — protected.',
  },
  llm_in_flight: {
    label: 'during LLM',
    tone: 'amber',
    tip: 'Caller spoke while LLM was generating the response. Cascade window — LLM completes normally.',
  },
  autocorrect: {
    label: 'during autocorrect',
    tone: 'amber',
    tip: 'Caller spoke during autocorrect (~1-2s). Cascade window — protected.',
  },
  post_audio: {
    label: 'post-audio (caller still listening)',
    tone: 'amber',
    tip: "Server finished streaming the bot's response but the caller is still hearing the tail over the telephony lag (~1-2s). Protected to avoid a redundant re-ask while the caller is still listening.",
  },
  waiting_first_audio: {
    label: 'first message',
    tone: 'blue',
    tip: "Caller spoke before the bot's opening message reached them. Ignored to protect the intro.",
  },
  idle: {
    label: 'idle (ANOMALY — report)',
    tone: 'red',
    tip: 'Classifier blocked this utterance but no bot turn was active. Backend should never produce this combination — please share the call ID with the voicex team.',
  },
};

const buildBadges = (
  msg: TranscriptMessageType,
  chainLength?: number,
): Badge[] => {
  const badges: Badge[] = [];

  if (msg.interrupted) {
    badges.push({
      label: msg.interruption_type === 'before_audio' ? '✂️ Not heard' : '✂️ Interrupted',
      color: 'amber',
      title: msg.interruption_type === 'before_audio'
        ? 'LLM produced text but no audio reached the caller'
        : 'User interrupted mid-audio',
    });
  }
  if (msg.combined_interrupted_message) {
    badges.push({ label: '🔗 Combined', color: 'blue', title: 'Multiple speech segments combined into one turn' });
  }
  if (msg.buffered) {
    badges.push({ label: `⏸ Buffered${msg.buffered_count ? ` ×${msg.buffered_count + 1}` : ''}`, color: 'slate', title: 'Held while LLM was responding' });
  }
  if (msg.debounced) {
    badges.push({ label: `⏱ Debounced${msg.debounce_count ? ` ×${msg.debounce_count}` : ''}`, color: 'slate', title: 'Multiple STT finals merged by debounce timer' });
  }
  if (msg.system_fallback) {
    badges.push({ label: '⚠️ Fallback', color: 'red', title: msg.fallback_reason || 'System-injected utterance' });
  }
  if (msg.autocorrected) {
    badges.push({ label: '✏️ Autocorrected', color: 'indigo', title: 'STT output was auto-corrected' });
  }
  if (msg.hallucination_detected) {
    badges.push({ label: '⚠️ STT loop', color: 'red', title: 'Repetitive STT output collapsed' });
  }
  if (msg.cancelled_before_response) {
    badges.push({ label: '⊘ Cancelled', color: 'grey', title: 'LLM was cancelled before producing output' });
  }
  if (msg.llm_response_cancelled) {
    badges.push({
      label: '⊘ LLM cancelled',
      color: 'grey',
      title: msg.llm_cancel_reason || 'LLM task was cancelled mid-response — user turn preserved',
    });
  }
  if (msg.llm_response_rejected) {
    const reason = msg.llm_rejection_reason
      ? ` Reason: ${msg.llm_rejection_reason}.`
      : '';
    badges.push({
      label: '⊘ bot response dropped',
      color: 'amber',
      title: `Caller said this, bot generated a response, but the response was dropped before audio played.${reason} See the paired assistant entry.`,
    });
  }

  // Intent classifier — keepalive / backchannel / noise were intentionally NOT
  // sent to the LLM. Surface them so the transcript doesn't look like silence.
  const intent = msg.intent_class;
  if (intent === 'keepalive' || intent === 'backchannel' || intent === 'noise') {
    const countSuffix =
      msg.keepalive_count != null && msg.keepalive_count > 1
        ? ` ×${msg.keepalive_count}`
        : '';
    const tooltipMap: Record<string, string> = {
      keepalive: 'Classifier blocked — caller said this but the bot was speaking. Not routed to the LLM.',
      backchannel: 'Classifier blocked — short acknowledgment sound ("uhh", "mhm"). Not routed to the LLM.',
      noise: 'Classifier blocked — non-speech noise. Not routed to the LLM.',
    };
    const countNote =
      msg.keepalive_count != null && msg.keepalive_count > 1
        ? ` Repeated ${msg.keepalive_count} times within this turn (capped at 10).`
        : '';
    // Phase context: each phase has its own label / tone / tooltip in
    // PHASE_META. audio_playback = normal overlap; everything else is a
    // cascade window — we want reviewers to spot those at a glance.
    // idle is special-cased separately below (anomaly badge).
    const phase = msg.bot_phase;
    const phaseLast = msg.bot_phase_last;
    const phaseMeta = phase ? PHASE_META[phase] : undefined;
    const isCascade = !!phase && phase !== 'audio_playback' && phase !== 'idle';
    const phaseLabel = phaseMeta?.label ?? phase ?? '';
    const phaseTip = phaseMeta?.tip ?? '';
    const phaseLine = phase
      ? phaseLast
        ? `\nBurst spanned: ${phaseLast} → ${phase} (caller kept talking across phases).${phaseTip ? ' ' + phaseTip : ''}`
        : `\nBot phase: ${phaseLabel}.${phaseTip ? ' ' + phaseTip : ''}`
      : '';
    badges.push({
      label: `${intent}${countSuffix} · ${phaseLabel || (isCascade ? 'cascade' : 'overlap')}`,
      color: phaseMeta?.tone ?? (isCascade ? 'amber' : 'slate'),
      title: `${tooltipMap[intent]}${countNote}${phaseLine}`,
    });
    // Anomaly: classifier blocked but no bot turn was active. Surface as a
    // separate top-level red badge so reviewers don't miss it among the
    // regular intent chips. Should never fire in healthy operation.
    if (phase === 'idle') {
      badges.push({
        label: '⚠️ Anomaly',
        color: 'red',
        title:
          'Classifier blocked this utterance but no bot turn was active when the gate fired. ' +
          'Backend should never produce this combination — share the call ID with the voicex team.',
      });
    }
  } else if (intent === 'post_completion_ack') {
    badges.push({
      label: 'acknowledged',
      color: 'slate',
      title: 'Caller said "thanks" / "ndatenda" — bot follow-up expected, not a new question.',
    });
  } else if (intent === 'directive') {
    badges.push({
      label: 'directive',
      color: 'blue',
      title: 'Short imperative ("stop", "wait") — this cancelled the bot.',
    });
  }

  // audio_delivered: partial_<N>ms — caller heard part of the bot reply before
  // this user turn cut it off.
  const partialMatch =
    typeof msg.audio_delivered === 'string'
      ? msg.audio_delivered.match(/^partial_(\d+)ms$/)
      : null;
  if (partialMatch) {
    badges.push({
      label: `bot audio cut at ${partialMatch[1]}ms`,
      color: 'amber',
      title: 'Caller heard partial bot audio before this turn interrupted it.',
    });
  }
  if (msg.frt_ms != null) {
    const frt = msg.frt_ms;
    const stages: Array<[string, number | undefined]> = [
      ['STT', msg.stt_ms],
      ['STT pure', msg.stt_processing_pure_ms],
      ['STT backlog', msg.stt_backlog_ms],
      ['Audio dur', msg.audio_stream_duration_ms],
      ['Autocorrect', msg.autocorrect_ms],
      ['Debounce', msg.debounce_ms],
      ['Hallucination', msg.hallucination_ms],
      ['LLM', msg.llm_ms],
      ['Synth', msg.synth_ms],
      ['Silent', msg.silent_ms],
    ];
    const breakdown = stages
      .filter(([, v]) => v != null)
      .map(([k, v]) => `${k}: ${v}ms`)
      .join('\n');

    badges.push({
      label: `⏱ ${formatMs(frt)}`,
      color: 'slate',
      title: `FRT: ${frt}ms${msg.turn_kind ? ` (${msg.turn_kind})` : ''}${breakdown ? '\n' + breakdown : ''}`,
    });
  }
  if (msg.stt_backlog_ms != null && msg.stt_backlog_ms > 0) {
    badges.push({
      label: `STT backlog ${formatMs(msg.stt_backlog_ms)}`,
      color: 'slate',
      title: 'How far the STT server was behind real-time when emitting this final. Reported STT latency includes any server-side lag, not just pure decode cost.',
    });
  }
  if (chainLength != null && chainLength >= 2) {
    badges.push({
      label: `🔗 Chain ×${chainLength}`,
      color: 'amber',
      title: `This reply landed after ${chainLength} user turns — earlier attempts were rejected, cancelled, or interrupted. Reported FRT may be inflated; clean FRT above is the wall-clock gap from the last user turn.`,
    });
  }
  if (msg.is_filler) {
    badges.push({ label: '💬 Filler', color: 'slate', title: 'Pre-ack / filler reply — excluded from LLM context' });
  }
  // The dedicated "Caller did not hear this" banner handles request_rejected_pre_audio.
  if (msg.error && msg.error_type !== 'request_rejected_pre_audio') {
    badges.push({
      label: `ℹ️ Notice${msg.error_type ? `: ${msg.error_type}` : ''}`,
      color: 'blue',
      title: typeof msg.error === 'string' ? msg.error : msg.error_type || 'Error',
    });
  }
  // Keyboard-input can appear either as a top-level function_name (when function_call: true)
  // or as an entry inside functionsInvoked[]. Surface it once, regardless of source.
  if (hasKeyboardInput(msg)) {
    badges.push({
      label: '⌨️ Keyboard input',
      color: 'amber',
      title: 'AI is waiting for DTMF / keypad input from the caller',
    });
  }

  // Non-keyboard functions from functionsInvoked[]
  if (msg.functionsInvoked && msg.functionsInvoked.length > 0) {
    msg.functionsInvoked
      .filter((fn) => !isKeyboardInputFn(fn))
      .forEach((fn) =>
        badges.push({ label: `⚡ ${fn}`, color: 'purple', title: 'Function/tool invoked' }),
      );
  }

  // Use-case status. Single backend-derived field; 'normal' renders no badge
  // so we don't clutter every assistant turn. Falls through to the raw
  // additionalInfo flags if bot_reply_status is absent (older payloads).
  const status = msg.bot_reply_status ?? deriveStatusFromAdditionalInfo(msg.additionalInfo);
  if (status === 'call_ending') {
    badges.push({
      label: '📞 Call ending',
      color: 'amber',
      title:
        'The bot will hang up after this response plays (callEnded=true). ' +
        'The reply is delivered in full, then the line drops.',
    });
  } else if (status === 'use_case_finished') {
    badges.push({
      label: '✓ Use case finished',
      color: 'blue',
      title:
        'The bot marked the inferred use case as complete on this turn. The call continues — ' +
        'caller may ask a follow-up or hang up themselves. Useful for measuring per-use-case turn count.',
    });
  }

  return badges;
};

// Fallback for older history payloads that carry the raw flags but no
// bot_reply_status derived field. Mirrors the backend precedence order.
const deriveStatusFromAdditionalInfo = (
  addl: TranscriptMessageType['additionalInfo'],
): string | undefined => {
  if (!addl) return undefined;
  if (addl.callEnded) return 'call_ending';
  if (addl.useCaseFinished) return 'use_case_finished';
  return 'normal';
};

const isKeyboardInputFn = (fn: string | undefined | null): boolean =>
  !!fn && fn.replace(/[_\s-]/g, '').toLowerCase() === 'keyboardinput';

const hasKeyboardInput = (msg: TranscriptMessageType): boolean =>
  isKeyboardInputFn(msg.function_name) ||
  !!msg.functionsInvoked?.some(isKeyboardInputFn);

const buildDetailRows = (msg: TranscriptMessageType, showRawStt: boolean): DetailRow[] => {
  const rows: DetailRow[] = [];

  if (msg.interrupted_previous_message) {
    rows.push({ label: 'Carried forward from prior turn', value: maskSensitiveNumbers(msg.interrupted_previous_message) });
  }
  if (msg.buffered_segments && msg.buffered_segments.length > 0) {
    rows.push({ label: 'Buffered segments', value: msg.buffered_segments.map(maskSensitiveNumbers).join(' · ') });
  }
  if (msg.debounced_segments && msg.debounced_segments.length > 0) {
    rows.push({ label: 'Debounced segments', value: msg.debounced_segments.map(maskSensitiveNumbers).join(' · ') });
  }
  if (msg.fallback_word) {
    rows.push({ label: 'Injected word', value: maskSensitiveNumbers(msg.fallback_word) });
  }
  if (msg.original_bot_prompt) {
    rows.push({ label: 'Bot prompt before silence', value: maskSensitiveNumbers(msg.original_bot_prompt) });
  }
  if (showRawStt && msg.original_transcription) {
    rows.push({ label: 'Raw STT (before correction)', value: maskSensitiveNumbers(msg.original_transcription) });
  }
  if (msg.llm_response_rejected) {
    if (msg.llm_produced_output != null) {
      rows.push({
        label: 'LLM produced output',
        value: msg.llm_produced_output ? 'yes — text discarded before delivery' : 'no — LLM hung or yielded nothing',
      });
    }
    if (msg.audio_delivered) {
      rows.push({ label: 'Audio delivered', value: msg.audio_delivered });
    }
  }
  if (showRawStt && msg.hallucination_sample) {
    rows.push({ label: 'Hallucination sample', value: maskSensitiveNumbers(msg.hallucination_sample) });
    if (msg.hallucination_original_word_count != null && msg.hallucination_cleaned_word_count != null) {
      rows.push({
        label: 'Word count',
        value: `${msg.hallucination_original_word_count} → ${msg.hallucination_cleaned_word_count}`,
      });
    }
  }

  // Use-case raw flags. Only surface when at least one is present so non-
  // use-case-aware assistant turns stay uncluttered.
  const addl = msg.additionalInfo;
  if (addl && (
    addl.useCaseSupported != null ||
    addl.useCaseFinished != null ||
    addl.callEnded != null ||
    addl.inferredUseCase != null
  )) {
    if (addl.inferredUseCase) {
      rows.push({ label: 'Inferred use case', value: addl.inferredUseCase });
    }
    if (addl.useCaseSupported != null) {
      rows.push({ label: 'Use case supported', value: addl.useCaseSupported ? 'yes' : 'no' });
    }
    if (addl.useCaseFinished != null) {
      rows.push({ label: 'Use case finished', value: addl.useCaseFinished ? 'yes' : 'no' });
    }
    if (addl.callEnded != null) {
      rows.push({ label: 'Bot requested hangup', value: addl.callEnded ? 'yes' : 'no' });
    }
  }

  return rows;
};

const renderContent = (msg: TranscriptMessageType): React.ReactNode => {
  const text = maskSensitiveNumbers(msg.content || '');
  const markers = [
    '[NOT HEARD BY USER]',
    '[INTERRUPTED]',
    '[NO RESPONSE]',
    '[REJECTED — not heard by user]',
  ];

  const tokens: React.ReactNode[] = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    const match = markers
      .map((m) => ({ marker: m, idx: remaining.indexOf(m) }))
      .filter((m) => m.idx !== -1)
      .sort((a, b) => a.idx - b.idx)[0];

    if (!match) {
      tokens.push(<span key={key++}>{remaining}</span>);
      break;
    }

    if (match.idx > 0) {
      tokens.push(<span key={key++}>{remaining.slice(0, match.idx)}</span>);
    }
    tokens.push(
      <span
        key={key++}
        className="inline-block px-1.5 py-0.5 mx-1 rounded text-[10px] font-semibold bg-red-100 text-red-700 border border-red-200 align-middle"
      >
        {match.marker.replace(/[[\]]/g, '')}
      </span>,
    );
    remaining = remaining.slice(match.idx + match.marker.length);
  }

  return tokens;
};

const Badge: React.FC<{ badge: Badge }> = ({ badge }) => (
  <span
    title={badge.title}
    className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${BADGE_STYLES[badge.color] || BADGE_STYLES.grey}`}
  >
    {badge.label}
  </span>
);

const DetailsExpander: React.FC<{ rows: DetailRow[] }> = ({ rows }) => {
  const [open, setOpen] = useState(false);
  if (rows.length === 0) return null;

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-[10px] text-gray-500 hover:text-gray-700"
      >
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        Details ({rows.length})
      </button>
      {open && (
        <div className="mt-1 space-y-1 pl-4 border-l border-gray-200">
          {rows.map((row, i) => (
            <div key={i} className="text-[10px]">
              <span className="font-semibold text-gray-600">{row.label}:</span>{' '}
              <span className="text-gray-700 break-words">{row.value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const TranscriptMessage: React.FC<TranscriptMessageProps> = ({
  message,
  showRawStt = false,
  chainLength,
}) => {
  const isBeforeAudio = message.interruption_type === 'before_audio';
  const isPlaceholder = message.cancelled_placeholder;
  const isRejectedPreAudio = message.error_type === 'request_rejected_pre_audio';
  // Heavy-dim = opacity-50 + dashed border + collapsed-by-default with Expand toggle.
  // Rejected-pre-audio gets its own banner + strikethrough body instead — leaving
  // the dropped text legible for QA review.
  const isHeavyDimmed = isBeforeAudio || !!isPlaceholder;

  const intent = message.intent_class;
  const isIntentBlocked =
    message.role === 'user' &&
    (intent === 'keepalive' || intent === 'backchannel' || intent === 'noise');

  const [expanded, setExpanded] = useState(!isHeavyDimmed);

  // SIDES, THE WAY EVERY CHAT PUTS THEM: the caller on the right, the agent on
  // the left. They were the other way round — assistant `items-end`, user
  // `items-start` — which is why this read as two columns of log lines rather
  // than a conversation: the eye expects the person being helped on the right,
  // and reversing that makes every turn feel like it belongs to the other
  // speaker. Squared-off `rounded` blocks with a 2px side border did the rest.
  //
  // The bubble's flattened corner marks the speaker's side, which is what
  // carries the reading when colour is unavailable — printed, or for a reader
  // who cannot separate the two hues.
  const roleConfig = {
    assistant: {
      bg: 'bg-white border border-gray-200 dark:bg-gray-700 dark:border-gray-600',
      label: 'AI agent',
      labelColor: 'text-gray-500 dark:text-gray-400',
      align: 'items-start',
      radius: 'rounded-2xl rounded-tl-sm',
      maxWidth: 'max-w-[85%]',
    },
    user: {
      bg: 'bg-indigo-50 border border-indigo-100 dark:bg-indigo-500/20 dark:border-indigo-500/30',
      label: 'Caller',
      labelColor: 'text-indigo-700 dark:text-indigo-300',
      align: 'items-end',
      radius: 'rounded-2xl rounded-tr-sm',
      maxWidth: 'max-w-[85%]',
    },
    system: {
      bg: 'bg-gray-100 dark:bg-gray-800',
      label: 'System',
      labelColor: 'text-gray-500 dark:text-gray-400',
      align: 'items-center',
      radius: 'rounded-lg',
      maxWidth: 'w-full',
    },
  } as const;

  const config = roleConfig[message.role] || roleConfig.system;
  const badges = buildBadges(message, chainLength);
  const detailRows = buildDetailRows(message, showRawStt);
  const awaitingKeypress = hasKeyboardInput(message);

  // For rejected-pre-audio assistant turns, swap the bubble's purple right-border
  // for red so the row scans as "did not reach caller" before reading the banner.
  const bubbleBg = isRejectedPreAudio
    ? 'bg-red-50 border border-red-300 dark:bg-red-900/20 dark:border-red-500/40'
    : config.bg;

  return (
    <div className={`flex flex-col ${config.align}`}>
      <div
        className={`px-3 py-2 ${config.radius} text-xs ${bubbleBg} ${config.maxWidth} ${
          isHeavyDimmed ? 'opacity-50 border-dashed' : ''
        } ${awaitingKeypress ? 'ring-2 ring-amber-400' : ''}`}
      >
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`font-semibold uppercase text-[10px] ${config.labelColor}`}>
              {config.label}
            </span>
            {isHeavyDimmed && (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="text-[10px] text-gray-500 hover:text-gray-700 underline"
              >
                {expanded ? 'Collapse' : 'Expand'}
              </button>
            )}
          </div>
          <span className="text-[10px] text-gray-500 ml-2 shrink-0">
            {formatTimestamp(message.timestamp)}
          </span>
        </div>

        {badges.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-1.5">
            {badges.map((b, i) => (
              <Badge key={i} badge={b} />
            ))}
          </div>
        )}

        {isRejectedPreAudio && (
          <div
            className="text-[10px] font-semibold text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1 mb-1.5"
            title="LLM produced this response but a new caller transcript arrived during the synth window, so playback was dropped before audio reached the caller. Reason: request_rejected_pre_audio. If this fires frequently, file a bug."
          >
            ⛔ Caller did not hear this
          </div>
        )}

        {expanded && (
          <>
            {message.autocorrected && message.original_transcription && (
              <p className="text-gray-400 line-through text-[11px] mb-0.5">
                {maskSensitiveNumbers(message.original_transcription)}
              </p>
            )}
            <p
              className={`text-gray-800 dark:text-gray-100 whitespace-pre-wrap break-words ${
                message.role === 'system' ? 'text-center italic' : ''
              } ${isIntentBlocked ? 'italic opacity-70' : ''} ${
                isRejectedPreAudio ? 'line-through text-gray-500' : ''
              }`}
            >
              {renderContent(message)}
            </p>

            {message.audio_duration_heard != null && message.audio_duration_heard > 0 && (
              <div className="mt-1 text-[10px] text-gray-600">
                🔊 heard ~{message.audio_duration_heard.toFixed(1)}s
              </div>
            )}

            <DetailsExpander rows={detailRows} />
          </>
        )}
      </div>
    </div>
  );
};

export default TranscriptMessage;
